import { useEffect, useState } from 'react'

import { Popover, TextInput } from '@mantine/core'
import { MonthPicker } from '@mantine/dates'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import IconCalendar from 'src/icons/IconCalendar'

dayjs.extend(utc)

const MonthYearInput = ({
  editing = true,
  value,
  onChange,
  label,
  required,
  className,
  error,
  maxDate,
  minDate,
  disabled,
  ...props
}: {
  editing?: boolean
  value?: Date | null
  onChange?: (date: Date | null) => void
  label?: string
  required?: boolean
  className?: string
  error?: string
  maxDate?: Date
  minDate?: Date
  disabled?: boolean
  [key: string]: any
}) => {
  const [inputValue, setInputValue] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [pickerOpened, setPickerOpened] = useState(false)

  // Update input value when value prop changes
  useEffect(() => {
    if (value) {
      const formatted = dayjs(value).format('MM/YYYY')
      setInputValue(formatted)
    } else {
      setInputValue('')
    }
  }, [value])

  // Format input as MM/YYYY while typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value.replace(/\D/g, '') // Remove non-numeric characters

    if (inputValue.length > 6) inputValue = inputValue.slice(0, 6) // Restrict to 6 characters (MMYYYY)

    const formattedValue = inputValue.replace(
      /^(\d{2})(\d{0,4})$/,
      (_, m, y) => {
        let result = m
        if (y) result += '/' + y
        return result
      }
    )

    setInputValue(formattedValue)

    // If we have a complete date (MM/YYYY), try to parse it
    if (formattedValue.length === 7) {
      const cleaned = formattedValue.replace(/\D/g, '')
      const month = cleaned.slice(0, 2)
      const year = cleaned.slice(2, 6)
      const monthNum = parseInt(month, 10)

      // Validate month (01-12)
      if (monthNum < 1 || monthNum > 12) {
        setValidationError('Invalid month')
        return
      }

      const parsedDate = dayjs(`${year}-${month}-01`).toDate()

      // Validate min date
      if (minDate) {
        const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1)
        const inputMonth = new Date(
          parsedDate.getFullYear(),
          parsedDate.getMonth(),
          1
        )
        if (inputMonth < minMonth) {
          setValidationError(
            `Date must be ${dayjs(minDate).format('MM/YYYY')} or later`
          )
          return
        }
      }

      // Validate max date
      const effectiveMaxDate = maxDate || new Date()
      const maxMonth = new Date(
        effectiveMaxDate.getFullYear(),
        effectiveMaxDate.getMonth(),
        1
      )
      const inputMonth = new Date(
        parsedDate.getFullYear(),
        parsedDate.getMonth(),
        1
      )

      if (inputMonth > maxMonth) {
        setValidationError(
          `Date must be ${dayjs(effectiveMaxDate).format('MM/YYYY')} or earlier`
        )
        return
      }

      // Date is valid — clear any previous error and update
      setValidationError(null)
      if (onChange) {
        onChange(dayjs(parsedDate).startOf('month').toDate())
      }
    } else if (formattedValue.length === 0 && onChange) {
      setValidationError(null)
      onChange(null)
    }
  }

  // On blur, revert to the last valid value if there's a validation error or partial input
  const handleBlur = () => {
    if (validationError || (inputValue.length > 0 && inputValue.length < 7)) {
      if (value) {
        setInputValue(dayjs(value).format('MM/YYYY'))
      } else {
        setInputValue('')
      }
      setValidationError(null)
    }
  }

  // Handle MonthPicker changes
  const handlePickerChange = (date: Date | null) => {
    setValidationError(null)
    if (date && onChange) {
      // Mantine MonthPicker returns UTC midnight dates. Extract the UTC
      // year/month to avoid local timezone shifting the month backward.
      const utcDate = dayjs.utc(date)
      onChange(new Date(utcDate.year(), utcDate.month(), 1))
      setPickerOpened(false)
    } else if (!date && onChange) {
      onChange(null)
      setPickerOpened(false)
    }
  }

  if (!editing) {
    return (
      <TextInput
        label={label}
        value={inputValue || '-'}
        variant="unstyled"
        disabled
        className={className}
        classNames={{
          label: 'text-sm font-medium text-doubleNickel-gray-700',
          input: 'text-md text-doubleNickel-gray-900',
        }}
        {...props}
      />
    )
  }

  return (
    <Popover
      opened={pickerOpened}
      onChange={setPickerOpened}
      position="bottom-start"
      withArrow
    >
      <Popover.Target>
        <TextInput
          label={label}
          required={required}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder="MM/YYYY"
          error={validationError || error}
          maxLength={7}
          className={className}
          rightSection={
            <div
              onClick={() => !disabled && setPickerOpened(!pickerOpened)}
              className={disabled ? 'cursor-default opacity-50' : 'cursor-pointer'}
            >
              <IconCalendar className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
            </div>
          }
          classNames={{
            label: 'text-sm font-medium text-doubleNickel-gray-700',
            input: 'text-md text-doubleNickel-gray-900',
          }}
          {...props}
        />
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <MonthPicker
          minDate={minDate || new Date('1900-01-01')}
          maxDate={
            maxDate ||
            (() => {
              const today = new Date()
              return new Date(today.getFullYear(), today.getMonth(), 1)
            })()
          }
          value={value}
          onChange={handlePickerChange}
        />
      </Popover.Dropdown>
    </Popover>
  )
}

export default MonthYearInput
